import React from "react";
import { Briefcase, CircleHelp, Moon, Plus, Search, Settings, Sun, UserCog, Wallet } from "lucide-react";

import NotificationsBell from "@/components/organisms/NotificationsBell";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useAppStore, useDarkMode } from "@/store/appStore";
import { cn } from "@/utils";

/**
 * PageBar — горизонтальная полоса с поиском и quick-actions
 * (workspace-навигация + уведомления + помощь + create CTA).
 *
 *   [🔍 Поиск ⌘K]   ↦   [📁][💳][👥][⚙][🔔][?][+]
 */
export const PageBar: React.FC = () => {
  const { setCurrentPage } = useAppStore();
  const darkMode = useDarkMode();
  const setThemePreference = useAppStore((state) => state.setThemePreference);
  // Кнопка «Сотрудники» в этой полосе ведёт на admin StaffPage
  // (платформенный персонал Инфорензик). Для клиента она бесполезна
  // и возвращает 403 — у клиента сотрудники организации доступны
  // через «Моя организация».
  const isStaff = useIsStaff();

  return (
    <div className="mb-7 flex flex-wrap items-center gap-2">
      {/* Search input — slim pill, left side */}
      <button
        type="button"
        className={cn(
          "order-last flex items-center gap-2 h-9 w-full px-3 rounded sm:order-none sm:max-w-[360px]",
          "bg-surface-elevated border border-line text-ink-3 text-fs-sm",
          "hover:border-line-strong transition-colors",
        )}
      >
        <Search size={14} className="shrink-0" />
        <span className="flex-1 text-left truncate">Поиск проверок, субъектов, модулей…</span>
        <span className="font-mono text-fs-2xs text-ink-4 inline-flex gap-0.5">
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <div className="hidden flex-1 sm:block" />

      {/* Workspace + global actions */}
      <div className="ml-auto flex items-center gap-0.5 sm:ml-0 sm:gap-1">
        <IconBtn tip="Моя организация" onClick={() => setCurrentPage("organization_dashboard")}>
          <Briefcase size={16} strokeWidth={1.75} />
        </IconBtn>
        <IconBtn tip="Биллинг" onClick={() => setCurrentPage("organization_billing")}>
          <Wallet size={16} strokeWidth={1.75} />
        </IconBtn>
        {isStaff && (
          <IconBtn tip="Сотрудники" onClick={() => setCurrentPage("staff_page")}>
            <UserCog size={16} strokeWidth={1.75} />
          </IconBtn>
        )}
        <IconBtn tip="Настройки" onClick={() => setCurrentPage("settings")}>
          <Settings size={16} strokeWidth={1.75} />
        </IconBtn>
        {/* Тема — рядом с настройками, а не плавающей кнопкой поверх таблиц */}
        <IconBtn
          tip={darkMode ? "Светлая тема" : "Тёмная тема"}
          onClick={() => setThemePreference(darkMode ? "light" : "dark")}
        >
          {darkMode ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
        </IconBtn>

        <NotificationsBell />
        <IconBtn tip="Помощь">
          <CircleHelp size={16} strokeWidth={1.75} />
        </IconBtn>

        {/* Primary CTA — оранжевый круг 16px внутри 36×36 hit-area */}
        <button
          type="button"
          onClick={() => setCurrentPage("complex_check")}
          aria-label="Создать"
          title="Создать"
          className={cn(
            "group relative grid place-items-center w-9 h-9 rounded-full ml-1",
            "transition-colors hover:bg-accent-faint",
          )}
        >
          <span className="absolute w-4 h-4 rounded-full bg-accent group-hover:bg-accent-hover transition-colors" />
          <Plus size={12} strokeWidth={2.5} className="relative text-on-accent" />
        </button>
      </div>
    </div>
  );
};

const IconBtn: React.FC<{
  tip: string;
  hasDot?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ tip, hasDot, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={tip}
    title={tip}
    className={cn(
      "relative grid place-items-center w-9 h-9 rounded",
      "text-ink-2 hover:text-ink-1 hover:bg-surface-hover transition-colors",
    )}
  >
    {children}
    {hasDot && (
      <span className="absolute top-2 right-[9px] w-1.5 h-1.5 rounded-full bg-accent ring-[1.5px] ring-surface-canvas" />
    )}
  </button>
);

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="px-1.5 py-px rounded-[5px] border border-line bg-surface-canvas font-mono text-fs-2xs text-ink-4">
    {children}
  </kbd>
);

export default PageBar;
